import type { LucideIcon } from 'lucide-react';
import {
  Briefcase,
  Building2,
  Cloud,
  DollarSign,
  Factory,
  GraduationCap,
  Heart,
  Home,
  Leaf,
  Music,
  Scale,
  ShoppingCart,
  Smartphone,
  TrendingUp,
  Truck,
  Users,
  Utensils,
  Zap,
} from 'lucide-react';

export type IndustryEnum =
  | 'b2b_saas'
  | 'consumer_tech'
  | 'fintech'
  | 'healthcare_biotech'
  | 'ecommerce_retail'
  | 'consulting'
  | 'marketing_agency'
  | 'manufacturing'
  | 'education_edtech'
  | 'media_entertainment'
  | 'real_estate'
  | 'energy_cleantech'
  | 'logistics'
  | 'government_nonprofit'
  | 'food_hospitality'
  | 'agriculture'
  | 'legal'
  | 'hr_recruiting'
  | 'other';

export interface NicheOption {
  id: IndustryEnum;
  label: string;
  icon: LucideIcon;
  description: string;
}

/**
 * Options must match the backend IndustryEnum exactly.
 * The `id` field is the value sent to the API.
 */
export const NICHE_OPTIONS: NicheOption[] = [
  {
    id: 'b2b_saas',
    label: 'B2B SaaS',
    icon: Cloud,
    description: 'Software-as-a-service for businesses',
  },
  {
    id: 'consumer_tech',
    label: 'Consumer Tech',
    icon: Smartphone,
    description: 'Consumer-facing technology products',
  },
  {
    id: 'fintech',
    label: 'Fintech',
    icon: DollarSign,
    description: 'Financial technology and services',
  },
  {
    id: 'healthcare_biotech',
    label: 'Healthcare & Biotech',
    icon: Heart,
    description: 'Healthcare, biotech, and life sciences',
  },
  {
    id: 'ecommerce_retail',
    label: 'E-Commerce & Retail',
    icon: ShoppingCart,
    description: 'Online and offline retail',
  },
  {
    id: 'consulting',
    label: 'Consulting',
    icon: Briefcase,
    description: 'Business and management consulting',
  },
  {
    id: 'marketing_agency',
    label: 'Marketing Agency',
    icon: TrendingUp,
    description: 'Marketing, advertising, and PR agencies',
  },
  {
    id: 'manufacturing',
    label: 'Manufacturing',
    icon: Factory,
    description: 'Industrial and product manufacturing',
  },
  {
    id: 'education_edtech',
    label: 'Education & EdTech',
    icon: GraduationCap,
    description: 'Education and learning technology',
  },
  {
    id: 'media_entertainment',
    label: 'Media & Entertainment',
    icon: Music,
    description: 'Media, film, music, and entertainment',
  },
  {
    id: 'real_estate',
    label: 'Real Estate',
    icon: Home,
    description: 'Property and real estate',
  },
  {
    id: 'energy_cleantech',
    label: 'Energy & CleanTech',
    icon: Zap,
    description: 'Energy, renewables, and clean technology',
  },
  {
    id: 'logistics',
    label: 'Logistics',
    icon: Truck,
    description: 'Supply chain and logistics',
  },
  {
    id: 'government_nonprofit',
    label: 'Government & Nonprofit',
    icon: Building2,
    description: 'Public sector and nonprofit organizations',
  },
  {
    id: 'food_hospitality',
    label: 'Food & Hospitality',
    icon: Utensils,
    description: 'Food service, restaurants, and hospitality',
  },
  {
    id: 'agriculture',
    label: 'Agriculture',
    icon: Leaf,
    description: 'Farming and agricultural technology',
  },
  {
    id: 'legal',
    label: 'Legal',
    icon: Scale,
    description: 'Legal services and law firms',
  },
  {
    id: 'hr_recruiting',
    label: 'HR & Recruiting',
    icon: Users,
    description: 'Human resources and talent acquisition',
  },
];

/** Set of valid niche IDs for runtime validation */
export const VALID_NICHE_IDS: ReadonlySet<string> = new Set(
  NICHE_OPTIONS.map(o => o.id)
);
